import React, { useEffect } from 'react';
import { useUIStore } from '@/store/uiStore';
import { debug } from '@/utils/debug';
import styles from './Toast.module.css';

interface ToastProps {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  message: string;
  duration?: number;
}

export function Toast({ id, type, message }: ToastProps) {
  const hideNotification = useUIStore(state => state.hideNotification);
  
  // Ensure message is always a string
  const safeMessage = typeof message === 'string' ? message : 
    typeof message === 'object' && message !== null ? JSON.stringify(message) : 
    String(message);
  
  useEffect(() => {
    debug.log('Toast: Showing notification', { id, type, message: safeMessage });
  }, [id, type, safeMessage]);
  
  const getIcon = () => {
    switch (type) {
      case 'success':
        return '✓';
      case 'error':
        return '✕';
      case 'warning':
        return '!';
      case 'info':
        return 'i';
    }
  };
  
  const handleDismiss = () => {
    hideNotification(id);
  };
  
  return (
    <div className={`${styles.toast} ${styles[type]}`}>
      <span className={styles.icon}>{getIcon()}</span>
      <p className={styles.message}>{safeMessage}</p>
      <button 
        className={styles.dismiss} 
        onClick={handleDismiss}
        aria-label="Dismiss"
      >
        ✕
      </button>
    </div>
  );
}

export function ToastContainer() {
  const notifications = useUIStore(state => state.notifications);
  
  if (notifications.length === 0) {
    return null;
  }
  
  return (
    <div className={styles.container}>
      {notifications.map(notification => (
        <Toast
          key={notification.id!}
          id={notification.id!}
          type={notification.type}
          message={notification.message}
          duration={notification.duration}
        />
      ))}
    </div>
  );
}